/**
 * Hero Component
 * Opulent luxury redesign — dark backdrop, centered layout, animated gold accents
 */

export function createHero(): string {
  return `
    <!-- ════════════════════════════════════
         HERO — OPULENT REDESIGN
    ════════════════════════════════════ -->
    <section id="home" class="hero-opulent grain" style="
      min-height: 85vh;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      padding-top: 140px;
      position: relative;
      overflow: hidden;
      background: #000000;
    ">

      <!-- ── Animated Glow Orbs ── -->
      <div class="hero-orb hero-orb-1" aria-hidden="true"></div>
      <div class="hero-orb hero-orb-2" aria-hidden="true"></div>
      <div class="hero-orb hero-orb-3" aria-hidden="true"></div>

      <!-- ── Gold Geometric SVG Frame ── -->
      <svg class="hero-geo" aria-hidden="true"
        viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg">

        <!-- Corner brackets -->
        <path d="M0 110 L0 0 L110 0"  stroke="#C9A227" stroke-width="1.2" fill="none" opacity="0.28"/>
        <path d="M1440 110 L1440 0 L1330 0" stroke="#C9A227" stroke-width="1.2" fill="none" opacity="0.28"/>
        <path d="M0 790 L0 900 L110 900" stroke="#C9A227" stroke-width="1.2" fill="none" opacity="0.16"/>
        <path d="M1440 790 L1440 900 L1330 900" stroke="#C9A227" stroke-width="1.2" fill="none" opacity="0.16"/>

        <!-- Top centre diamond -->
        <polygon points="720,22 744,52 720,82 696,52"
          stroke="#C9A227" stroke-width="0.9" fill="none" opacity="0.32"/>

        <!-- Diagonal accent lines — left -->
        <line x1="0"   y1="360" x2="360" y2="0"   stroke="#C9A227" stroke-width="0.45" opacity="0.09"/>
        <line x1="0"   y1="560" x2="560" y2="0"   stroke="#C9A227" stroke-width="0.45" opacity="0.06"/>

        <!-- Diagonal accent lines — right -->
        <line x1="1440" y1="360" x2="1080" y2="0"  stroke="#C9A227" stroke-width="0.45" opacity="0.09"/>
        <line x1="1440" y1="560" x2="880"  y2="0"  stroke="#C9A227" stroke-width="0.45" opacity="0.06"/>

        <!-- Faint bottom arc -->
        <ellipse cx="720" cy="920" rx="520" ry="200"
          stroke="#C9A227" stroke-width="0.5" fill="none" opacity="0.06"/>
      </svg>

      <!-- ── Floating Gold Particles ── -->
      <div class="hero-particles" aria-hidden="true">
        <div class="hero-particle" style="left:7%;  top:22%; animation-delay:0s;    animation-duration:6.2s;"></div>
        <div class="hero-particle" style="left:14%; top:64%; animation-delay:1.3s;  animation-duration:7.1s;"></div>
        <div class="hero-particle" style="left:21%; top:38%; animation-delay:0.6s;  animation-duration:5.8s;"></div>
        <div class="hero-particle" style="left:32%; top:78%; animation-delay:2.1s;  animation-duration:6.7s;"></div>
        <div class="hero-particle" style="left:48%; top:12%; animation-delay:3.2s;  animation-duration:8.0s;"></div>
        <div class="hero-particle" style="left:55%; top:84%; animation-delay:0.9s;  animation-duration:6.4s;"></div>
        <div class="hero-particle" style="left:68%; top:29%; animation-delay:1.7s;  animation-duration:7.3s;"></div>
        <div class="hero-particle" style="left:76%; top:61%; animation-delay:0.4s;  animation-duration:5.5s;"></div>
        <div class="hero-particle" style="left:84%; top:44%; animation-delay:2.8s;  animation-duration:6.9s;"></div>
        <div class="hero-particle" style="left:92%; top:18%; animation-delay:1.1s;  animation-duration:7.6s;"></div>
        <div class="hero-particle hero-particle-lg" style="left:4%;  top:50%; animation-delay:2.6s;  animation-duration:9.2s;"></div>
        <div class="hero-particle hero-particle-lg" style="left:96%; top:70%; animation-delay:0.7s;  animation-duration:8.4s;"></div>
        <div class="hero-particle hero-particle-lg" style="left:50%; top:55%; animation-delay:4.0s;  animation-duration:10s;"></div>
      </div>

      <!-- ── Main Centred Content ── -->
      <div style="
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        z-index: 10;
        padding: 48px 48px 32px;
      ">
        <div style="text-align: center; max-width: 1020px; width: 100%;">

          <!-- Eyebrow with flanking rules -->
          <div class="reveal" style="
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 28px;
            margin-bottom: 48px;
          ">
            <span style="display:block; width:80px; height:1px;
              background: linear-gradient(to right, transparent, rgba(201,162,39,0.8));"></span>
            <span style="
              font-family: 'Inter', sans-serif;
              font-size: 12px;
              font-weight: 600;
              letter-spacing: 0.38em;
              text-transform: uppercase;
              color: #C9A227;
            ">Arceus Wealth</span>
            <span style="display:block; width:80px; height:1px;
              background: linear-gradient(to left, transparent, rgba(201,162,39,0.8));"></span>
          </div>

          <!-- H1 — three lines for perfect centred stacking -->
          <h1 class="reveal d1" style="
            font-family: 'Cormorant Garamond', serif;
            font-size: clamp(52px, 7.2vw, 110px);
            font-weight: 500;
            line-height: 1.08;
            letter-spacing: -0.015em;
            color: #F8F7F4;
            margin-bottom: 42px;
            text-align: center;
          ">
            Investing in long term growth<br>
            with <em style="color:#C9A227; font-style:italic;">Enduring</em><br>
            Potential
          </h1>

          <!-- Animated gold shimmer divider -->
          <div class="reveal d2" style="display:flex; justify-content:center; margin-bottom:36px;">
            <div class="hero-shimmer-line"></div>
          </div>

          <!-- Subheadline -->
          <p class="reveal d2" style="
            font-family: 'Inter', sans-serif;
            font-size: clamp(16px, 1.4vw, 19px);
            line-height: 1.7;
            color: rgba(248,247,244,0.5);
            max-width: 540px;
            margin: 0 auto 48px;
            font-weight: 400;
            letter-spacing: 0.01em;
            text-align: center;
          ">
            Arceus partners with strong individuals to unlock growth and create lasting enterprise value.
          </p>

          <!-- CTAs — symmetrically centred -->
          <div class="reveal d3" style="
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
            align-items: center;
            justify-content: center;
          ">
            <a href="/contact.html" class="hero-btn-primary">
              Contact Us
              <svg width="14" height="9" viewBox="0 0 14 9" fill="none" style="margin-left:10px; flex-shrink:0;">
                <path d="M1 4.5H13M9 1L13 4.5L9 8" stroke="currentColor" stroke-width="1.4"
                  stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </a>
            <a href="#strategy" class="hero-btn-secondary">Our Strategy</a>
          </div>

        </div>
      </div>

      <!-- ── Vertical Scroll Indicator ── -->
      <div class="reveal d4" style="
        position: absolute;
        bottom: 40px;
        right: 56px;
        z-index: 10;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 14px;
      ">
        <span style="
          font-family: 'Inter', sans-serif;
          font-size: 10px;
          letter-spacing: 0.38em;
          text-transform: uppercase;
          color: rgba(201,162,39,0.45);
          writing-mode: vertical-rl;
          text-orientation: mixed;
        ">Scroll</span>
        <div class="hero-scroll-line"></div>
      </div>

    </section>
  `;
}

export default createHero;
